/**
 * Section « À traiter » de l'accueil : la file unique (`queue.ts`), bornée à
 * quelques lignes, le reste résumé par famille vers sa liste. Les gestes qui
 * existent se font depuis la ligne — prendre en charge une urgence, accepter
 * ou refuser une absence, reporter un rappel — avec leur retour : en attente,
 * en file (hors ligne), confirmé ou échoué.
 */
import type { ReactNode } from 'react';
import {
  AlertTriangle, BookOpen, CalendarClock, CalendarOff, ClipboardCheck, ClipboardList, Clock, EuroIcon, FilePen,
  FlaskConical, GitMerge, Inbox, ListTodo, Megaphone, Package, ShieldAlert, Target, TimerOff, UserX,
  type LucideIcon,
} from 'lucide-react';
import { RecordIcon, RecordList, RecordRow } from '../../components/RecordRow.js';
import { Button, Spinner } from '../../components/ui.js';
import { Banner, ErrorState, resultTone } from '../../components/ui/Feedback.js';
import { ButtonLink } from '../../components/ui/action.js';
import { Section } from '../../components/ui/Section.js';
import { t } from '../../core/i18n.js';
import type { AckState } from '../emergencies/emergency-ack.js';
import { splitQueue, type QueueItem, type QueueKind } from './queue.js';

const ICONS: Record<QueueKind, LucideIcon> = {
  urgence: ShieldAlert,
  sla: CalendarClock,
  demande: Inbox,
  retard: Clock,
  oubli: TimerOff,
  non_affectee: UserX,
  conflit: AlertTriangle,
  sync: GitMerge,
  absence: CalendarOff,
  objectif: Target,
  tache: ListTodo,
  facture: EuroIcon,
  devis: FilePen,
  rapport: ClipboardList,
  conformite: FlaskConical,
  document: BookOpen,
  annonce: Megaphone,
  rappel: ClipboardCheck,
  stock: Package,
  anomalie: Package,
};

/** L'icône d'une famille de « À traiter », la même sur la ligne et sur la carte « Maintenant ». */
export function QueueIcon({ kind, size = 20 }: { kind: QueueKind; size?: number }): ReactNode {
  const Icon = ICONS[kind];
  return <Icon size={size} />;
}

/** Retour d'un geste fait depuis l'accueil, affiché en tête de la section. */
export interface QueueFeedback {
  key: string;
  state: 'queued' | 'confirmed' | 'failed';
  text: string;
  /** Annuler le geste quand c'est possible (rappel reporté). */
  undo?: () => void;
}

export interface QueueActions {
  ackStates: Record<string, AckState>;
  /** Demande la prise en charge (la confirmation est ouverte par la page). */
  onAck: (alertId: string, title: string) => void;
  decidingAbsence: string | undefined;
  onAccept: (requestId: string, name: string) => void;
  onRefuse: (requestId: string) => void;
  onLater: (reminderId: string) => void;
}

function RowActions({ item, actions }: { item: QueueItem; actions: QueueActions }): ReactNode {
  const act = item.act;
  if (!act) return null;
  if (act.kind === 'prise_en_charge') {
    const state = actions.ackStates[act.alertId];
    // En file ou confirmée : le retour est dit en tête de section, le bouton n'a plus lieu d'être.
    if (state && (state.status === 'queued' || state.status === 'confirmed')) return null;
    const pending = state?.status === 'pending';
    return (
      <>
        <ButtonLink to={item.to} variant="ghost">Détails</ButtonLink>
        <Button variant="secondary" disabled={pending} onClick={() => actions.onAck(act.alertId, item.detail)}>
          {pending ? 'Prise en charge…' : t('accueil.prendreEnCharge')}
        </Button>
      </>
    );
  }
  if (act.kind === 'absence') {
    const busy = actions.decidingAbsence !== undefined;
    return (
      <>
        <Button variant="ghost" disabled={busy} ariaLabel={`Refuser l’absence de ${act.name}`}
          onClick={() => actions.onRefuse(act.requestId)}>
          {t('commun.refuser')}
        </Button>
        <Button variant="secondary" disabled={busy} ariaLabel={`Accepter l’absence de ${act.name}`}
          onClick={() => actions.onAccept(act.requestId, act.name)}>
          {actions.decidingAbsence === act.requestId ? 'Envoi…' : 'Accepter'}
        </Button>
      </>
    );
  }
  return null;
}

function QueueRow({ item, actions }: { item: QueueItem; actions: QueueActions }): ReactNode {
  const later = item.act?.kind === 'plus_tard' ? item.act.reminderId : undefined;
  const rowActions = <RowActions item={item} actions={actions} />;
  const hasActions = item.act?.kind === 'prise_en_charge' || item.act?.kind === 'absence';
  return (
    <li>
      <RecordRow
        className="home-queue__row"
        to={item.to}
        state={{ from: '/' }}
        urgent={item.severity === 'critique'}
        lead={<RecordIcon><QueueIcon kind={item.kind} /></RecordIcon>}
        title={item.title}
        // Une seule ligne de contexte : le moment puis le dossier, coupée net au téléphone.
        lines={[[item.sinceLabel, item.detail].filter(Boolean).join(' · ')]}
        singleLine
        {...(later ? { menu: [{ label: 'Plus tard (demain)', onSelect: () => actions.onLater(later) }] } : {})}
        {...(hasActions ? { actions: rowActions } : {})}
      />
    </li>
  );
}

export function HomeQueue(props: {
  items: QueueItem[];
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  limit: number;
  actions: QueueActions;
  feedback: QueueFeedback[];
  onCloseFeedback: (key: string) => void;
}): ReactNode {
  const { shown, hidden, lists } = splitQueue(props.items, props.limit);
  const empty = props.items.length === 0;
  // Rien à traiter et rien à dire : pas de section vide, la carte « Maintenant » rassure déjà.
  if (empty && props.feedback.length === 0 && !props.error && !props.loading) return null;
  return (
    <Section id="a-traiter" title={t('accueil.aTraiter')} {...(props.loading ? {} : { count: props.items.length })} flush>
      {props.feedback.map((entry) => entry.undo ? (
        <Banner key={entry.key} tone="info" action={{ label: 'Annuler', onClick: entry.undo }}
          onDismiss={() => props.onCloseFeedback(entry.key)}>
          {entry.text}
        </Banner>
      ) : (
        <Banner tone={resultTone(entry.state)} key={entry.key} onDismiss={() => props.onCloseFeedback(entry.key)}>
          {entry.text}
        </Banner>
      ))}
      {props.loading ? <Spinner variant="section" label="Chargement de ce qui est à traiter" /> : null}
      {props.error && !props.loading ? <ErrorState error={props.error} onRetry={props.onRetry} /> : null}
      {shown.length > 0 && !props.loading ? (
        <RecordList label={t('accueil.aTraiter')}>
          {shown.map((item) => <QueueRow key={item.key} item={item} actions={props.actions} />)}
        </RecordList>
      ) : null}
      {hidden.length + lists.length > 0 && !props.loading ? (
        // Le reste, à un geste ; une famille dont la liste est pleine mène aussi à sa liste complète.
        <Section id="a-traiter-suite"
          title={hidden.length === 0 ? 'Listes complètes' : hidden.length === 1 ? 'Voir l’autre' : `Voir les ${hidden.length} autres`}
          collapsible flush>
          <RecordList label="Autres choses à traiter">
            {hidden.map((item) => <QueueRow key={item.key} item={item} actions={props.actions} />)}
            {lists.map((list) => (
              <li key={`liste:${list.kind}`}>
                <RecordRow
                  className="home-queue__list"
                  to={list.to}
                  lead={<RecordIcon><QueueIcon kind={list.kind} /></RecordIcon>}
                  title={list.label}
                />
              </li>
            ))}
          </RecordList>
        </Section>
      ) : null}
    </Section>
  );
}

